// LimitationList.tsx
import React, { useState } from 'react';
import { 
    Box, 
    CircularProgress, 
    Typography
} from '@mui/material';
import { 
    GridColDef,
    GridRenderCellParams, 
} from '@mui/x-data-grid';
import LocalizedGrid, { CustomGridColDef } from '../../localizedGrid/LocalizedGridCook3h';
import { useFetch } from '../../../hooks/useFetch3';
import { api as entityApi } from '../../../api/entityApi';
import { Subtenant, subtenantApi, SubtenantQuota } from '../../../api/subtenantApi';
import { Entity } from '../../../types/entity';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import ActionButtons from '../../ActionButtons2';
import { FormModal } from '../../modal/FormModal';
import LimitationForm from './LimitationForm';
import { formatDateTime } from '../../../tools/util';
import { useAuthStore } from '../../../store/useAuthStore3';

export const LimitationList: React.FC = () => {
    const { t } = useTranslation();
    const { data: quota = [], setData, loading: quotaLoading, error } = useFetch<SubtenantQuota[]>( subtenantApi.getQuotas, []);
    const { data: entity = [], loading: entityLoading } = useFetch<Entity[]>(entityApi.get, [], { type: 1 });
    const { data: subtenant = [], loading: subtenantLoading } = useFetch<Subtenant[]>( subtenantApi.get, []);
    const [selectedQuota, setSelectedQuota] = useState<SubtenantQuota | null>(null);    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { subtenantId } = useAuthStore();
    

    const columns: GridColDef[] = [
        { 
            field: 'entity_id', 
            headerName: "Об'ект", 
            flex: 1, 
            type: "singleSelect",
            valueOptions: entity.map( e => ({value: e.entity_id, label: e.name})),
            editable: false,
            renderCell: (params: GridRenderCellParams<SubtenantQuota>) => {
                const entityName = entity.find(e => e.entity_id === params.row.entity_id)?.name;
                return (
                <Typography variant="body2">
                    {entityName || `ID: ${params.row.entity_id}`}
                </Typography>
                );
            }
        },
        { 
            field: 'subtenant_id', 
            headerName: 'Орендар', 
            flex: 1, 
            type: "singleSelect",
            valueOptions: subtenant.map( s => ({value: s.subtenant_id, label: s.name})),
            editable: false,
            renderCell: (params: GridRenderCellParams<SubtenantQuota>) => {
                const subtenantName = subtenant.find(e => e.subtenant_id === params.row.subtenant_id)?.name;
                return (
                    <Typography variant="body2">
                        {subtenantName || `ID: ${params.row.subtenant_id}`}
                    </Typography>
                );
            }
        },
        { field: 'max_parking_slots', headerName: 'Max паркомісць', type: 'number', flex: 1, },
        { field: 'max_permits', headerName: 'Max перепусток', type: 'number', flex: 1, },
        { field: 'occupied_parking_slots', headerName: 'Факт паркомісць', type: 'number', flex: 1, },
        { field: 'ts_update', headerName: 'Момент фактування', type: 'string', flex: 1, valueFormatter: (v) => formatDateTime(v)},
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<SubtenantQuota>) => {
                return !subtenantId ? (
                    <ActionButtons actions={[
                        {type: 'edit', action: () => handleOpenModal(params.row), },
                    ]} />
                ) : (<></>);
            },
        },        
    ];

    const handleOpenModal = (quota: SubtenantQuota | null) => {
        setSelectedQuota(quota);
        setIsModalOpen(true);
    };
    

    if (quotaLoading || entityLoading || subtenantLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
                <CircularProgress />
            </Box>
        );
    }

    const handleUpdate = async (edited: SubtenantQuota) => {
        await subtenantApi.updateSingleQuotaRow(edited);
        setData(prev => prev.map(p => 
            p.subtenant_id === edited.subtenant_id && 
            p.entity_id === edited.entity_id ? 
            edited : p));
        setIsModalOpen(false);
    };

    return (
        <Box sx={{ width: '100%', bgcolor: 'background.paper', p: 2 }}>
            <Typography variant="h4">
                Обмеження
            </Typography>
            <LocalizedGrid
                lang={i18n.language as any}
                gridId="subtenant-limitations-grid"
                rows={quota}
                columns={columns}
                getRowId={(row) => row.subtenant_id +'/'+row.entity_id}
                disableRowSelectionOnClick
            />

            <Typography color='error'>{error?.message}</Typography>

            <FormModal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <LimitationForm
                    quota={selectedQuota}
                    subtenants={subtenant}
                    entities={entity}
                    onSave={handleUpdate}
                    onCancel={() => setIsModalOpen(false)}
                />
            </FormModal>

        </Box>
    );
};